import React from 'react';

import Label from '@components/inputs/text/Label';
import { countrySpecificCurrency, formatDateToDD_MMM_YYYY, convertMinorToMajorCurrency } from '@utils/misc/functions';
import InfoBox from '@components/cards/messageBox/InfoBox';
import { countrySpecificDistanceUnit } from '@utils/misc/functions';
import { formatCurrency } from '../../utils/misc/functions';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Button from '@components/buttons/Button/Button';
import { useNavigate, useParams } from 'react-router';
import { SelectedSubscriptionState } from './selectedSubscriptionReducer';
import { CancellationReasonState } from './cancellationReasonReducer';
import { CancellationVehicleState } from './cancellationVehicleReducer';
import { CancellationChargesState } from './cancellationChargesReducer';
import { LongFeeNames, FOV2WeeklyVehicleFee } from '@services/subscription/SubscriptionService';


export type CancellationPersonalT = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
};


export type CancellationAddressT = {
  street: string;
  city: string;
  state: string;
  postcode: string;
};


type Props = {
    navigateTarget: string;
    selectedSubscriptionState: SelectedSubscriptionState;
    cancellationReasonState: CancellationReasonState;
    cancellationVehicleState: CancellationVehicleState;
    cancellationPersonal: CancellationPersonalT;
    cancellationAddress: CancellationAddressT;
    cancellationChargesState: CancellationChargesState;
};

export default function CancellationReview({
                                               navigateTarget,
                                               selectedSubscriptionState,
                                               cancellationReasonState,
                                               cancellationVehicleState,
                                               cancellationPersonal,
                                               cancellationAddress,
                                               cancellationChargesState
                                           }: Props) {
    const navigate = useNavigate();
    const { country } = useParams();

    const totalAncillaryCharges = convertMinorToMajorCurrency(
        cancellationChargesState.ancillary_charges.reduce(
            (total, charge) => total + (charge.fee || 0),
            0
        )
    );

    const paymentHolidayCharges = convertMinorToMajorCurrency(
        cancellationChargesState.calculated_charges.find(
            charge => charge.long_fee_name === LongFeeNames.PaymentHolidayGapFee
        )?.fee || 0
    );

    const noticePeriodCharge = convertMinorToMajorCurrency(
        cancellationChargesState.calculated_charges.find(
            charge => charge.long_fee_name === LongFeeNames.NoticePeriodFee
        )?.fee || 0
    );

    const totalCancellationCharges = convertMinorToMajorCurrency(
        cancellationChargesState.calculated_charges.reduce(
            (total, charge) => total + (charge.fee || 0),
            0
        )
    ) + totalAncillaryCharges;

    const renderChargeSection = (label: any, value: any) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
                <Label text={label} styleCfg={{ bold: true }} />
            </div>
            <p style={{ margin: 0 }} >{value || ''}</p>
        </div>
    );

    const CreditForUnusedDatesInfoBox = () => (
        <div style={{ margin: '1.5vh 3.5vw' }}>
            <InfoBox message='Credit for unused days will be refunded to customer if applicable in due course' />
        </div>
    );

    const ZohoDeskTicketNonRemarketableInfoBox = () => (
        <div style={{ margin: '1.5vh 3.5vw' }}>
            <InfoBox message='Zoho Desk ticket will be generated for Finance Review as Vehicle is not Remarketable' />
        </div>
    );

    const AdditionalChargesInfoBox = ({ country }: { country: string }) => (
        <div style={{ margin: '1.5vh 3.5vw' }}>
            <InfoBox message={`Additional charges for excess ${countrySpecificDistanceUnit(country)} and infringements may be charged subsequently if applicable`} />
        </div>
    );

    // Find the Flexi Own Cancellation Fee in the calculated charges
    const flexiOwnCharge = cancellationChargesState.calculated_charges.find(
        charge => charge.long_fee_name === LongFeeNames.WeeklyVehicleChargeFee
    ) as FOV2WeeklyVehicleFee | undefined;

    const flexiOwnCancellationFee = !flexiOwnCharge || flexiOwnCharge.vehicle_remarketing_fee === 0
        ? 'N/A'
        : convertMinorToMajorCurrency(flexiOwnCharge.fee).toString();

    const handleSubmit = () => {
        navigate(navigateTarget) // To result screen (also handles api call to process)
    };

    return (
        <>
            <NavBar pageTitle="Cancellation - Review" />
            <div style={{ margin: '2.5vh 2.5vw' }}>
                <p>Please review ensuring all key details are correct ahead of processing cancellation</p>
                <div style={{ margin: '2.5vh 0vw' }}>
                    {renderChargeSection(`First name`, cancellationPersonal.firstName)}
                    {renderChargeSection(`Last name`, cancellationPersonal.lastName)}
                    {renderChargeSection(`Email`, cancellationPersonal.email)}
                    {renderChargeSection(`Address`, cancellationAddress.street + ', ' + cancellationAddress.city + ', ' + cancellationAddress.postcode)}
                </div>
                <div style={{ margin: '2.5vh 0vw' }}>
                    {renderChargeSection(`Vehicle ID`, cancellationVehicleState.vehicleId)}
                    {renderChargeSection(`Registration plate`, cancellationVehicleState.registrationPlate)}
                    {renderChargeSection(`Ending ${countrySpecificDistanceUnit(String(country))}`, cancellationVehicleState.endingKms)}
                    {renderChargeSection(`Fleetio Status`, cancellationVehicleState.fleetioStatusName)}
                </div>
                <div style={{ margin: '2.5vh 0vw' }}>
                    {renderChargeSection('Cancellation Type', cancellationReasonState.cancellationType)}
                    {renderChargeSection(`Cancellation Reason - Outcome`, cancellationReasonState.cancellationCategory)}
                    {renderChargeSection(`Cancellation Reason - Reason`, cancellationReasonState.cancellationSubCategory)}
                    {renderChargeSection(`Vehicle Remarketable`, cancellationReasonState.vehicleRemarketable === true ? "Yes" : cancellationReasonState.vehicleRemarketable === false ? "No" : "N/A")}
                    <div>
                        <Label text={"Additional Comments"} styleCfg={{ bold: true }} />
                    </div>
                    <div style={{ overflowWrap: 'break-word' }}>
                        <p style={{ margin: 0 }}>{cancellationReasonState.cancellationAdditionalInfo || ''}</p>
                    </div>
                    {renderChargeSection(`Customer returning date`, cancellationReasonState.customerReturnDate || "N/A")}
                    {renderChargeSection(`Add restriction to account`, cancellationReasonState.cancellationAddRestrictionToAccount || "N/A")}

                    {renderChargeSection(`Notice given`, selectedSubscriptionState.noticeGiven && formatDateToDD_MMM_YYYY(String(selectedSubscriptionState.noticeGiven)) || "N/A")}
                    {renderChargeSection(`Cancellation date`, formatDateToDD_MMM_YYYY(cancellationVehicleState.cancellationDate))}
                </div>
                <div style={{ margin: '2.5vh 0vw' }}>
                    {renderChargeSection(`Total ancillary fee (${countrySpecificCurrency(String(country))})`, formatCurrency(totalAncillaryCharges) || formatCurrency("0"))}
                    {renderChargeSection(`Flexi Own Cancellations Fee (${countrySpecificCurrency(String(country))})`, flexiOwnCancellationFee)}
                    {renderChargeSection(`Payment holiday charge (${countrySpecificCurrency(String(country))})`, formatCurrency(paymentHolidayCharges) || formatCurrency("0"))}
                    {renderChargeSection(`Notice period charge (${countrySpecificCurrency(String(country))})`, formatCurrency(noticePeriodCharge) || formatCurrency("0"))}
                    {renderChargeSection(`Total due today (${countrySpecificCurrency(String(country))})`, formatCurrency(totalCancellationCharges) || formatCurrency("0"))}
                </div>

                {cancellationReasonState.vehicleRemarketable === false && <ZohoDeskTicketNonRemarketableInfoBox />}

                <CreditForUnusedDatesInfoBox />

                <AdditionalChargesInfoBox country={String(country)} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5vh 0', marginTop: '2.5vh' }}>
                    <Button
                        label="Submit Cancellation"
                        onClickFunc={handleSubmit}
                    />
                </div>
            </div>
        </>
    );
};